import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildHubSearchPath, resolveSearchCategoryFromLocation } from '../lib/hubCategoryRoute';
import { withReturnState } from '../lib/goBack';
import { useMingo } from '../context/MingoContext';
import { formatStoredRegionLabel } from '../lib/regionDisplayLabel';
import { getLocationName } from '../api/locationApi';
import { flagEmojiForSelectedRegion, flagEmojiFromCountryCode } from '../utils/regionFlagEmoji';
import { useNotificationBadge } from '../hooks/useNotificationBadge';
import { useVisaHeaderBadge } from '../hooks/useVisaHeaderBadge';
import { useChatUnread } from '../hooks/useChatUnread';
import { TabLineIcon } from './icons/TabLineIcons';
import LocationBottomSheet from './LocationBottomSheet';

const LONG_PRESS_MS = 450;

/**
 * Touch: short tap → onTap, long press → onLongPress (filled slot).
 * Mouse: click → onTap, double-click → onLongPress (filled slot).
 * @param {{ onTap: () => void; onLongPress: () => void; delay?: number }} opts
 */
function useSlotPressHandlers({ onTap, onLongPress, delay = LONG_PRESS_MS }) {
  const longPressedRef = useRef(false);
  const suppressClickRef = useRef(false);
  const timerRef = useRef(null);
  const startPosRef = useRef({ x: 0, y: 0 });

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startLongPressTimer = useCallback(
    (x, y) => {
      startPosRef.current = { x, y };
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        longPressedRef.current = true;
        onLongPress();
      }, delay);
    },
    [clearTimer, delay, onLongPress],
  );

  const onPointerDown = useCallback(
    (e) => {
      if (e.pointerType !== 'touch') return;
      longPressedRef.current = false;
      suppressClickRef.current = false;
      startLongPressTimer(e.clientX, e.clientY);
    },
    [startLongPressTimer],
  );

  const onPointerMove = useCallback(
    (e) => {
      if (e.pointerType !== 'touch') return;
      const dx = Math.abs(e.clientX - startPosRef.current.x);
      const dy = Math.abs(e.clientY - startPosRef.current.y);
      if (dx > 12 || dy > 12) clearTimer();
    },
    [clearTimer],
  );

  const onPointerUp = useCallback(
    (e) => {
      if (e.pointerType !== 'touch') return;
      clearTimer();
      if (!longPressedRef.current) {
        suppressClickRef.current = true;
        onTap();
        e.preventDefault();
      }
      longPressedRef.current = false;
    },
    [clearTimer, onTap],
  );

  const onPointerCancel = useCallback(() => {
    clearTimer();
    longPressedRef.current = false;
  }, [clearTimer]);

  const onClick = useCallback(
    (e) => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.pointerType === 'touch') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onTap();
    },
    [onTap],
  );

  const onDoubleClick = useCallback(
    (e) => {
      e.preventDefault();
      onLongPress();
    },
    [onLongPress],
  );

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onClick,
    onDoubleClick,
  };
}

/**
 * @param {{
 *   isActive: boolean;
 *   isFilled: boolean;
 *   onTap: () => void;
 *   onReconfigure: () => void;
 *   ariaLabel: string;
 *   className: string;
 *   children: React.ReactNode;
 * }} props
 */
function RegionSlotTab({ isActive, isFilled, onTap, onReconfigure, ariaLabel, className, children }) {
  const press = useSlotPressHandlers({
    onTap,
    onLongPress: isFilled ? onReconfigure : () => {},
  });

  return (
    <button
      type="button"
      className={className}
      aria-label={ariaLabel}
      aria-current={isActive ? 'true' : undefined}
      onPointerDown={press.onPointerDown}
      onPointerMove={press.onPointerMove}
      onPointerUp={press.onPointerUp}
      onPointerCancel={press.onPointerCancel}
      onClick={press.onClick}
      onDoubleClick={isFilled ? press.onDoubleClick : undefined}
    >
      {children}
    </button>
  );
}

/**
 * Fixed top bar: menu + region | chat, search, notifications
 */
function MainAppHeader({ onOpenServiceMenu, onOpenDrawer, onWrite }) {
  const navigate = useNavigate();
  const location = useLocation();
  const hubCategory = resolveSearchCategoryFromLocation(location);
  const {
    currentLocation,
    t,
    language,
    activeSource,
    setActiveRegionSlot,
    setActiveSource,
    showToast,
    gpsSlot,
    gpsSlotLabel,
    pickSlot,
    pickSlotLabel,
    isGuest,
    isFeatureEnabled,
  } = useMingo();

  const goSearch = useCallback(() => {
    const params = new URLSearchParams(location.search || '');
    const qOnSearch =
      location.pathname === '/search' ? params.get('q') || params.get('query') || '' : '';
    const tabOnSearch = location.pathname === '/search' ? params.get('tab') || '' : '';
    const path = buildHubSearchPath({ q: qOnSearch, category: hubCategory, tab: tabOnSearch });
    const here = `${location.pathname}${location.search || ''}`;
    if (here === path) return;
    // Already on SearchPage: keep existing return path, just adjust query/scope.
    if (location.pathname === '/search') {
      navigate(path, { replace: true, state: location.state });
      return;
    }
    navigate(path, { state: withReturnState(location) });
  }, [hubCategory, location, navigate]);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetSlot, setSheetSlot] = useState(0);

  useEffect(() => {
    setActiveRegionSlot(activeSource === 'pick' ? 1 : 0);
  }, [activeSource, setActiveRegionSlot]);

  const openSheet = useCallback(
    (slot) => {
      setActiveRegionSlot(slot);
      setSheetSlot(slot);
      setSheetOpen(true);
    },
    [setActiveRegionSlot],
  );

  const primaryRegionLabel = formatStoredRegionLabel(
    gpsSlotLabel ||
      (currentLocation && getLocationName(currentLocation, language)) ||
      'Da Nang',
    t,
    language,
  );
  const primaryFlag = flagEmojiFromCountryCode(gpsSlot?.country_code || currentLocation?.country_code || 'VN');
  const secondaryRegionRaw = pickSlotLabel || pickSlot?.city_label || '';
  const secondaryRegionLabel = secondaryRegionRaw
    ? formatStoredRegionLabel(secondaryRegionRaw, t, language)
    : '';
  // pickSlot.country_code(실제 저장된 국가코드)를 우선 사용 — 라벨 텍스트 키워드 추측은
  // "충청북도"처럼 REGION_CITY_ROWS/정규식에 없는 한국 지명에서 죄다 베트남 국기로 잘못 떨어졌음
  const secondaryFlag = secondaryRegionRaw
    ? pickSlot?.country_code
      ? flagEmojiFromCountryCode(pickSlot.country_code)
      : flagEmojiForSelectedRegion(secondaryRegionRaw)
    : '';
  const isPickFilled = Boolean(pickSlot?.city_id || secondaryRegionRaw);
  const isGpsActive = activeSource !== 'pick';
  const isPickActive = activeSource === 'pick';

  const hapticTick = useCallback(() => {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(8);
    }
  }, []);

  const switchToSource = useCallback(
    (source, regionName) => {
      if (activeSource === source) return;
      setActiveSource(source);
      hapticTick();
      showToast(t('location.switchedTo', { region: regionName }), { ms: 1000 });
    },
    [activeSource, hapticTick, setActiveSource, showToast, t],
  );

  const handleGpsTap = useCallback(() => {
    if (!isGpsActive) {
      switchToSource('gps', primaryRegionLabel);
    }
  }, [isGpsActive, primaryRegionLabel, switchToSource]);

  const handlePickTap = useCallback(() => {
    if (!isPickFilled) {
      openSheet(1);
      return;
    }
    if (!isPickActive) {
      switchToSource('pick', secondaryRegionLabel || secondaryRegionRaw);
    }
  }, [isPickActive, isPickFilled, openSheet, secondaryRegionLabel, secondaryRegionRaw, switchToSource]);

  const unreadAlerts = useNotificationBadge();
  const visaBadge = useVisaHeaderBadge();
  const chatEnabled = isFeatureEnabled('chat');
  const { unreadTotal: chatUnread } = useChatUnread(isGuest);

  return (
    <div className="mingo-header-wrap mingo-main-header-wrap">
      <header className="mingo-header mingo-main-header" role="banner">
        <button
          type="button"
          className="mingo-main-header__hamburger mingo-header__icon-btn"
          aria-label={t('tabs.profile')}
          onClick={() => onOpenDrawer?.()}
        >
          <TabLineIcon name="user" />
        </button>
        <div className="mingo-main-header__dual-region" role="group" aria-label={t('location.selectRegion')}>
          <RegionSlotTab
            isActive={isGpsActive}
            isFilled
            onTap={handleGpsTap}
            onReconfigure={() => openSheet(0)}
            ariaLabel={`${t('location.gpsAutoDetected')}: ${primaryRegionLabel}`}
            className={`mingo-main-header__region-tab mingo-main-header__region-tab--primary-fixed ${
              isGpsActive ? 'mingo-main-header__region-tab--active' : 'mingo-main-header__region-tab--dimmed'
            }`}
          >
            <span className="mingo-main-header__region-gps-badge" aria-hidden="true">
              <TabLineIcon name="gps" size={10} color="currentColor" />
            </span>
            <span className="mingo-main-header__region-icon" aria-hidden="true">
              {primaryFlag}
            </span>
            <span className="mingo-main-header__region-tab-city">{primaryRegionLabel}</span>
          </RegionSlotTab>
          <div className="mingo-main-header__region-divider" aria-hidden="true" />
          <RegionSlotTab
            isActive={isPickActive}
            isFilled={isPickFilled}
            onTap={handlePickTap}
            onReconfigure={() => openSheet(1)}
            ariaLabel={secondaryRegionRaw ? t('location.secondaryRegion') : t('location.addRegion')}
            className={`mingo-main-header__region-tab ${
              isPickActive
                ? 'mingo-main-header__region-tab--active'
                : isPickFilled
                  ? 'mingo-main-header__region-tab--dimmed'
                  : 'mingo-main-header__region-tab--empty'
            }`}
          >
            {secondaryRegionRaw ? (
              <>
                <span className="mingo-main-header__region-icon" aria-hidden="true">
                  {secondaryFlag}
                </span>
                <span className="mingo-main-header__region-tab-city">{secondaryRegionLabel}</span>
              </>
            ) : (
              <span className="mingo-main-header__region-tab-add">+ {t('location.addRegion')}</span>
            )}
          </RegionSlotTab>
        </div>
        <div className="mingo-header__actions mingo-main-header__actions">
          <button
            type="button"
            className="mingo-header__icon-btn"
            aria-label={t('header.search')}
            onClick={goSearch}
          >
            <TabLineIcon name="search" />
          </button>
          {visaBadge ? (
            <button
              type="button"
              className={`mingo-header__visa-badge mingo-header__visa-badge--${visaBadge.kind}`}
              onClick={() => navigate('/settings/account')}
              aria-label={visaBadge.text}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              {visaBadge.Icon
                ? React.createElement(visaBadge.Icon, {
                    size: 10,
                    stroke: 2,
                    'aria-hidden': true,
                    style: { color: visaBadge.iconColor || 'currentColor', flexShrink: 0 },
                  })
                : null}
              {visaBadge.text}
            </button>
          ) : null}
          {chatEnabled ? (
            <button
              type="button"
              className="mingo-header__icon-btn mingo-header__icon-btn--notif"
              aria-label={t('tabs.chat')}
              onClick={() => navigate('/chats')}
            >
              <TabLineIcon name="chat" />
              {chatUnread > 0 ? (
                <span className="mingo-header__notif-badge" aria-hidden="true">
                  {chatUnread > 9 ? '9+' : chatUnread}
                </span>
              ) : null}
            </button>
          ) : null}
          <button
            type="button"
            className="mingo-header__icon-btn mingo-header__icon-btn--notif"
            aria-label={t('header.notifications')}
            onClick={() => navigate('/notifications')}
          >
            <TabLineIcon name="bell" />
            {unreadAlerts > 0 ? (
              <span className="mingo-header__notif-badge" aria-hidden="true">
                {unreadAlerts > 9 ? '9+' : unreadAlerts}
              </span>
            ) : null}
          </button>
          {onWrite && (
            <button
              type="button"
              className="mingo-header__icon-btn"
              onClick={onWrite}
              aria-label="글쓰기"
            >
              <TabLineIcon name="pencil" />
            </button>
          )}
        </div>
      </header>
      {sheetOpen ? (
        <LocationBottomSheet initialSlot={sheetSlot} open={sheetOpen} onClose={() => setSheetOpen(false)} />
      ) : null}
    </div>
  );
}

export default MainAppHeader;
